const sectionSelector = '.seal-fullscreen-slideshow';
const slideSelector = 'slideshow-slide';
const originalSlideSelector = ':scope > slideshow-slide:not([data-seal-loop-clone])';
const overlaySelector = '.seal-fullscreen-slideshow__slide-link';
const buttonSelector = '.seal-standard-slide__button[href]';
const deferredMediaSelector = 'template[data-seal-hero-media-template]';
const interactiveSelector = 'a, button, input, select, textarea, summary, [tabindex]';
const pointerDragThreshold = 6;
const loopSettleDelay = 600;
const wheelSettleDelay = 600;
const autoplayRestartDelay = 120;
const autoplayScrollMovementThreshold = 0.5;
const barRewindDuration = 180;

class SealLoopSlideshow extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.track = this.querySelector('[data-seal-loop-track]');
    this.currentIndex = 0;
    this.userPaused = false;
    this.isInteracting = false;
    this.isLoopJumping = false;
    this.scrollFrame = undefined;
    this.settleTimer = undefined;
    this.wheelEndTimer = undefined;
    this.wheelInteracting = false;
    this.autoplayTimer = undefined;
    this.autoplayRestartTimer = undefined;
    this.autoplayStartedAt = undefined;
    this.barRewindTimers = new Map();
    this.loopJumpFrame = undefined;
    this.resizeObserver = undefined;
    this.pointerDrag = undefined;
    this.interactionStartScrollLeft = undefined;
    this.lastAutoplayScrollLeft = undefined;
    this.suppressActivationUntil = 0;

    if (!(this.track instanceof HTMLElement)) return;

    this.removeClones();
    this.refreshSlides();
    if (!this.originalSlides.length) return;

    this.setupSlides();
    if (this.originalSlides.length > 1) this.setupLoop();
    this.bindEvents();
    this.updateMetrics();
    this.jumpToOriginal(0);
    this.updateCurrentIndex(0, { force: true });
    this.scheduleAutoplay();
    this.dataset.sealReady = 'true';
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.clearScrollFrame();
    this.clearSettleTimer();
    this.clearWheelEndTimer();
    this.clearAutoplayRestartTimer();
    this.clearAutoplayTimer({ rewind: false });
    this.clearBarRewinds();
    this.clearLoopJumpFrame();
  }

  get autoplayEnabled() {
    return this.hasAttribute('data-seal-autoplay') && this.originalSlides.length > 1;
  }

  get autoplayDelay() {
    const seconds = Number.parseInt(this.dataset.sealAutoplaySpeed || '', 10);
    return (Number.isFinite(seconds) ? seconds : 4) * 1000;
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  setupSlides() {
    this.originalSlides.forEach((slide, index) => {
      slide.dataset.sealLogicalIndex = `${index}`;
      slide.setAttribute('aria-hidden', `${index !== 0}`);
    });
  }

  setupLoop() {
    const lastIndex = this.originalSlides.length - 1;
    const beforeClone = this.createClone(this.originalSlides[lastIndex], lastIndex, 'before');
    const afterClone = this.createClone(this.originalSlides[0], 0, 'after');

    this.track.prepend(beforeClone);
    this.track.append(afterClone);
    this.refreshSlides();
  }

  createClone(slide, logicalIndex, position) {
    const clone = slide.cloneNode(true);
    clone.dataset.sealLoopClone = position;
    clone.dataset.sealLogicalIndex = `${logicalIndex}`;
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.setAttribute('data-nosnippet', '');
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');

    clone.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      this.replaceCloneElement(heading, 'div');
    });
    clone.querySelectorAll('a, button').forEach((control) => {
      this.replaceCloneElement(control, 'span');
    });

    [clone, ...clone.querySelectorAll('*')].forEach((element) => {
      element.removeAttribute('id');
      element.removeAttribute('ref');
      element.removeAttribute('data-shopify-editor-block');
      [...element.attributes].forEach((attribute) => {
        if (attribute.name.startsWith('on:')) element.removeAttribute(attribute.name);
      });
    });

    clone.querySelectorAll(interactiveSelector).forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    clone.querySelectorAll('video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.autoplay = false;
      video.preload = 'metadata';
      video.pause();
    });

    return clone;
  }

  replaceCloneElement(element, tagName) {
    const replacement = document.createElement(tagName);

    [...element.attributes].forEach((attribute) => {
      const keepAttribute =
        attribute.name === 'class' ||
        attribute.name === 'style' ||
        (attribute.name.startsWith('data-') && !attribute.name.startsWith('data-shopify-editor-'));

      if (keepAttribute) replacement.setAttribute(attribute.name, attribute.value);
    });

    replacement.append(...element.childNodes);
    element.replaceWith(replacement);
  }

  removeClones() {
    this.track?.querySelectorAll('[data-seal-loop-clone]').forEach((clone) => clone.remove());
  }

  refreshSlides() {
    this.originalSlides = [...(this.track?.querySelectorAll(originalSlideSelector) || [])];
    this.physicalSlides = [...(this.track?.children || [])].filter((slide) => slide.matches(slideSelector));
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.track.addEventListener('scroll', this.handleScroll, { passive: true, signal });
    this.track.addEventListener('scrollend', this.handleScrollEnd, { passive: true, signal });
    this.track.addEventListener('touchstart', this.handleInteractionStart, { passive: true, signal });
    this.track.addEventListener('touchend', this.handleInteractionEnd, { passive: true, signal });
    this.track.addEventListener('touchcancel', this.handleInteractionEnd, { passive: true, signal });
    this.track.addEventListener('wheel', this.handleWheel, { passive: true, signal });
    this.track.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.track.addEventListener('dragstart', this.handleNativeDragStart, { signal });
    this.track.addEventListener('keydown', this.handleKeydown, { signal });
    this.addEventListener('click', this.handleClickCapture, { capture: true, signal });

    document.addEventListener('pointermove', this.handlePointerMove, { signal });
    document.addEventListener('pointerup', this.handlePointerEnd, { passive: true, signal });
    document.addEventListener('pointercancel', this.handlePointerEnd, { passive: true, signal });
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });

    this.querySelector('[data-seal-loop-previous]')?.addEventListener('click', () => this.goRelative(-1), { signal });
    this.querySelector('[data-seal-loop-next]')?.addEventListener('click', () => this.goRelative(1), { signal });
    this.querySelector('[data-seal-loop-pause]')?.addEventListener('click', this.pause, { signal });
    this.querySelector('[data-seal-loop-play]')?.addEventListener('click', this.play, { signal });

    this.querySelectorAll('[data-seal-loop-index]').forEach((control) => {
      control.addEventListener(
        'click',
        () => {
          const index = Number.parseInt(control.dataset.sealLoopIndex || '', 10);
          if (Number.isFinite(index)) this.goTo(index, { userInitiated: true });
        },
        { signal }
      );
    });

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.track);
  }

  handleInteractionStart = () => {
    this.clearWheelEndTimer();
    this.wheelInteracting = false;
    this.normalizeBoundaryOffset();
    this.interactionStartScrollLeft = this.track.scrollLeft;
    this.lastAutoplayScrollLeft = this.track.scrollLeft;
    this.isInteracting = true;
    this.setAttribute('data-seal-interacting', '');
    this.clearSettleTimer();
    this.clearAutoplayRestartTimer();
    this.clearAutoplayTimer();
    this.hydrateBoundaryClones();
  };

  handleInteractionEnd = () => {
    const scrollDistance = Math.abs(this.track.scrollLeft - (this.interactionStartScrollLeft ?? this.track.scrollLeft));
    this.interactionStartScrollLeft = undefined;
    this.isInteracting = false;
    this.removeAttribute('data-seal-interacting');
    this.scheduleSettle();
    this.scheduleAutoplayRestart(scrollDistance > 1 ? autoplayRestartDelay : 0);
  };

  handleWheel = (event) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    if (!this.wheelInteracting) {
      this.normalizeBoundaryOffset();
      this.lastAutoplayScrollLeft = this.track.scrollLeft;
    }
    this.wheelInteracting = true;
    this.isInteracting = true;
    this.setAttribute('data-seal-interacting', '');
    this.clearSettleTimer();
    this.clearAutoplayRestartTimer();
    this.clearAutoplayTimer();
    this.hydrateBoundaryClones();
    this.scheduleWheelSettle();
    this.scheduleAutoplayRestart();
  };

  handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;

    if (event.button !== 0) return;
    this.handleInteractionStart();
    this.pointerDrag = {
      id: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.track.scrollLeft,
      moved: false,
    };
  };

  handlePointerMove = (event) => {
    const drag = this.pointerDrag;
    if (!drag || event.pointerId !== drag.id) return;

    const distance = drag.startX - event.clientX;
    if (!drag.moved && Math.abs(distance) > pointerDragThreshold) {
      drag.moved = true;
      this.setAttribute('dragging', '');
      if (this.track.setPointerCapture) {
        this.track.setPointerCapture(event.pointerId);
      }
    }

    if (!drag.moved) return;
    event.preventDefault();
    this.track.scrollLeft = drag.startScrollLeft + distance;
  };

  handleNativeDragStart = (event) => {
    if (this.pointerDrag) event.preventDefault();
  };

  handlePointerEnd = (event) => {
    const drag = this.pointerDrag;
    if (!drag || event.pointerId !== drag.id || event.pointerType === 'touch') return;

    if (drag.moved) this.suppressActivationUntil = Date.now() + 350;
    if (this.track.hasPointerCapture?.(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
    this.pointerDrag = undefined;
    this.removeAttribute('dragging');
    this.handleInteractionEnd();

    const nearest = this.getNearestPhysicalSlide();
    if (nearest) this.scrollToSlide(nearest.element);
  };

  handleClickCapture = (event) => {
    if (Date.now() >= this.suppressActivationUntil) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  handleScroll = () => {
    if (this.isLoopJumping || this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      const scrollLeft = this.track.scrollLeft;
      const scrollMoved =
        this.lastAutoplayScrollLeft === undefined ||
        Math.abs(scrollLeft - this.lastAutoplayScrollLeft) > autoplayScrollMovementThreshold;
      this.lastAutoplayScrollLeft = scrollLeft;

      if (scrollMoved) {
        if (this.wheelInteracting) this.isInteracting = true;
        this.setAttribute('data-seal-interacting', '');
        this.clearAutoplayTimer();
        this.scheduleAutoplayRestart();
      }

      const nearest = this.getNearestPhysicalSlide();
      if (nearest) this.updateCurrentIndex(nearest.logicalIndex);
      if (this.wheelInteracting) {
        this.scheduleWheelSettle();
      } else {
        this.scheduleSettle();
      }
    });
  };

  handleScrollEnd = () => {
    if (this.wheelInteracting) {
      this.scheduleWheelSettle();
      this.scheduleAutoplayRestart(0);
      return;
    }

    if (this.isInteracting) return;
    this.settle();
  };

  handleResize = () => {
    const index = this.currentIndex;
    this.updateMetrics();
    this.jumpToOriginal(index);
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.clearAutoplayRestartTimer();
      this.clearAutoplayTimer({ rewind: false });
    } else {
      this.scheduleAutoplay();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goRelative(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goRelative(1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.goTo(0, { userInitiated: true });
    } else if (event.key === 'End') {
      event.preventDefault();
      this.goTo(this.originalSlides.length - 1, { userInitiated: true });
    }
  };

  handleSlideLinkClick = (event) => {
    event.stopPropagation();
  };

  pause = () => {
    this.userPaused = true;
    this.setAttribute('paused', '');
    this.clearAutoplayRestartTimer();
    this.clearAutoplayTimer();
  };

  play = () => {
    this.userPaused = false;
    this.removeAttribute('paused');
    this.clearAutoplayRestartTimer();
    this.scheduleAutoplay();
  };

  updateMetrics() {
    this.refreshSlides();
    this.originalPositions = this.originalSlides.map((slide) => this.getSlidePosition(slide));
  }

  getSlidePosition(slide) {
    if (!(slide instanceof HTMLElement)) return 0;
    return slide.offsetLeft - this.track.offsetLeft;
  }

  getNearestPhysicalSlide() {
    if (!this.physicalSlides.length) return undefined;

    const referencePoint = this.track.scrollLeft + this.track.clientWidth / 2;
    let nearest;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.physicalSlides.forEach((slide) => {
      const slideCenter = this.getSlidePosition(slide) + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - referencePoint);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = {
          element: slide,
          logicalIndex: Number.parseInt(slide.dataset.sealLogicalIndex || '0', 10),
          clonePosition: slide.dataset.sealLoopClone,
        };
      }
    });

    return nearest;
  }

  goRelative(direction) {
    if (this.originalSlides.length <= 1) return;

    this.clearAutoplayRestartTimer();
    this.clearAutoplayTimer();
    const lastIndex = this.originalSlides.length - 1;

    if (direction > 0 && this.currentIndex === lastIndex) {
      this.scrollToSlide(this.track.querySelector('[data-seal-loop-clone="after"]'));
    } else if (direction < 0 && this.currentIndex === 0) {
      this.scrollToSlide(this.track.querySelector('[data-seal-loop-clone="before"]'));
    } else {
      this.goTo(this.currentIndex + direction, { userInitiated: true });
    }
  }

  goTo(index, { userInitiated = false } = {}) {
    if (!this.originalSlides.length) return;

    const normalizedIndex = Math.min(Math.max(index, 0), this.originalSlides.length - 1);
    this.clearAutoplayRestartTimer();
    this.clearAutoplayTimer();
    this.scrollToSlide(this.originalSlides[normalizedIndex]);
    this.updateCurrentIndex(normalizedIndex);

    if (userInitiated) this.hydrateAdjacentSlide(normalizedIndex);
  }

  scrollToSlide(slide) {
    if (!(slide instanceof HTMLElement)) return;

    this.track.scrollTo({
      left: this.getSlidePosition(slide),
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
    });
    this.scheduleSettle();
  }

  settle() {
    this.clearSettleTimer();
    if (this.isInteracting || this.isLoopJumping) return;

    const nearest = this.getNearestPhysicalSlide();
    if (!nearest) return;

    this.updateCurrentIndex(nearest.logicalIndex, { force: true });

    if (nearest.clonePosition) {
      this.jumpToOriginal(nearest.logicalIndex);
    }

    this.hydrateAdjacentSlide(nearest.logicalIndex);
    this.resumeAutoplayAfterSettle();
  }

  normalizeBoundaryOffset() {
    if (this.originalSlides.length <= 1 || this.isLoopJumping) return false;

    const nearest = this.getNearestPhysicalSlide();
    if (!nearest?.clonePosition) return false;

    const clonePosition = this.getSlidePosition(nearest.element);
    const originalPosition = this.originalPositions?.[nearest.logicalIndex];
    if (!Number.isFinite(clonePosition) || !Number.isFinite(originalPosition)) return false;

    this.updateCurrentIndex(nearest.logicalIndex, { force: true });
    this.jumpToPosition(this.track.scrollLeft + originalPosition - clonePosition);
    return true;
  }

  jumpToOriginal(index) {
    const targetPosition = this.originalPositions?.[index];
    if (!Number.isFinite(targetPosition)) return;

    this.jumpToPosition(targetPosition);
  }

  jumpToPosition(targetPosition) {
    this.isLoopJumping = true;
    this.setAttribute('data-seal-loop-jumping', '');
    this.lastAutoplayScrollLeft = targetPosition;
    this.track.scrollTo({ left: targetPosition, behavior: 'instant' });

    this.clearLoopJumpFrame();
    this.loopJumpFrame = requestAnimationFrame(() => {
      this.loopJumpFrame = requestAnimationFrame(() => {
        this.removeAttribute('data-seal-loop-jumping');
        this.isLoopJumping = false;
        this.loopJumpFrame = undefined;
      });
    });
  }

  updateCurrentIndex(index, { force = false } = {}) {
    if (!force && index === this.currentIndex) return;

    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    this.originalSlides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', `${slideIndex !== index}`);
    });

    this.querySelectorAll('[data-seal-loop-index]').forEach((control) => {
      control.setAttribute('aria-selected', `${Number.parseInt(control.dataset.sealLoopIndex || '', 10) === index}`);
    });

    const current = this.querySelector('[data-seal-loop-current]');
    if (current) current.textContent = `${index + 1}`;

    if (previousIndex !== index || !this.autoplayTimer) {
      this.resetBarAnimation();
    }

    if (force || previousIndex !== index) {
      this.dispatchEvent(
        new CustomEvent('slideshow:select', {
          bubbles: true,
          detail: {
            index,
            previousIndex,
            slide: this.originalSlides[index],
            trigger: 'seal-loop',
          },
        })
      );
    }
  }

  resetBarAnimation() {
    if (!this.querySelector('[data-seal-bars]')) return;

    this.setAttribute('data-seal-bars-resetting', '');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.removeAttribute('data-seal-bars-resetting'));
    });
  }

  scheduleSettle() {
    this.clearSettleTimer();
    this.settleTimer = window.setTimeout(() => this.settle(), loopSettleDelay);
  }

  scheduleAutoplay() {
    this.clearAutoplayRestartTimer();
    this.clearAutoplayTimer();
    if (!this.autoplayEnabled || this.userPaused || document.hidden || this.isInteracting) return;

    this.setAttribute('data-seal-autoplay-running', '');
    this.resetBarAnimation();
    this.autoplayStartedAt = Date.now();
    this.autoplayTimer = window.setTimeout(() => {
      this.autoplayTimer = undefined;
      this.goRelative(1);
    }, this.autoplayDelay);
  }

  scheduleAutoplayRestart(delay = autoplayRestartDelay) {
    this.clearAutoplayRestartTimer();
    if (!this.autoplayEnabled || this.userPaused || document.hidden) return;

    this.autoplayRestartTimer = window.setTimeout(() => {
      this.autoplayRestartTimer = undefined;
      if (this.isInteracting && !this.wheelInteracting) return;

      if (this.wheelInteracting) this.isInteracting = false;
      this.removeAttribute('data-seal-interacting');
      this.scheduleAutoplay();
    }, delay);
  }

  resumeAutoplayAfterSettle() {
    if (this.autoplayTimer) return;

    this.clearAutoplayRestartTimer();
    this.removeAttribute('data-seal-interacting');
    this.scheduleAutoplay();
  }

  hydrateAdjacentSlide(index) {
    hydrateHeroMedia(this.originalSlides[(index + 1) % this.originalSlides.length]);
  }

  hydrateBoundaryClones() {
    this.track.querySelectorAll('[data-seal-loop-clone]').forEach(hydrateHeroMedia);
  }

  clearScrollFrame() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = undefined;
  }

  clearSettleTimer() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = undefined;
  }

  clearWheelEndTimer() {
    if (this.wheelEndTimer) clearTimeout(this.wheelEndTimer);
    this.wheelEndTimer = undefined;
  }

  clearAutoplayRestartTimer() {
    if (this.autoplayRestartTimer) clearTimeout(this.autoplayRestartTimer);
    this.autoplayRestartTimer = undefined;
  }

  getCurrentBarControl() {
    return this.querySelector('[data-seal-loop-index][aria-selected="true"]');
  }

  rewindCurrentBar() {
    const control = this.getCurrentBarControl();
    if (!(control instanceof HTMLElement) || !Number.isFinite(this.autoplayStartedAt)) return;

    const progress = Math.min(Math.max((Date.now() - this.autoplayStartedAt) / this.autoplayDelay, 0), 1);
    const startTranslate = -101 + progress * 101;

    this.clearBarRewind(control);
    control.style.setProperty('--seal-slideshow-bar-rewind-start', `${startTranslate}%`);
    void control.offsetWidth;
    control.setAttribute('data-seal-bar-rewinding', '');

    const timer = window.setTimeout(() => this.clearBarRewind(control), barRewindDuration + 40);
    this.barRewindTimers.set(control, timer);
  }

  clearBarRewind(control) {
    if (!(control instanceof HTMLElement)) return;

    const timer = this.barRewindTimers.get(control);
    if (timer) clearTimeout(timer);
    this.barRewindTimers.delete(control);
    control.removeAttribute('data-seal-bar-rewinding');
    control.style.removeProperty('--seal-slideshow-bar-rewind-start');
  }

  clearBarRewinds() {
    this.barRewindTimers.forEach((timer) => clearTimeout(timer));
    this.barRewindTimers.clear();
    this.querySelectorAll('[data-seal-bar-rewinding]').forEach((control) => {
      control.removeAttribute('data-seal-bar-rewinding');
      control.style.removeProperty('--seal-slideshow-bar-rewind-start');
    });
  }

  scheduleWheelSettle() {
    this.clearWheelEndTimer();
    this.wheelEndTimer = window.setTimeout(() => {
      this.finishWheelInteraction();
      this.settle();
    }, wheelSettleDelay);
  }

  finishWheelInteraction() {
    this.clearWheelEndTimer();
    this.wheelInteracting = false;
    this.isInteracting = false;
    this.removeAttribute('data-seal-interacting');
  }

  clearAutoplayTimer({ rewind = true } = {}) {
    if (rewind && this.hasAttribute('data-seal-autoplay-running')) this.rewindCurrentBar();
    if (this.autoplayTimer) clearTimeout(this.autoplayTimer);
    this.autoplayTimer = undefined;
    this.autoplayStartedAt = undefined;
    this.removeAttribute('data-seal-autoplay-running');
  }

  clearLoopJumpFrame() {
    if (this.loopJumpFrame) cancelAnimationFrame(this.loopJumpFrame);
    this.loopJumpFrame = undefined;
  }
}

function hydrateHeroMedia(slide) {
  if (!(slide instanceof Element)) return;

  const template = slide.querySelector(deferredMediaSelector);
  if (!(template instanceof HTMLTemplateElement)) return;

  template.replaceWith(template.content.cloneNode(true));
  slide.dataset.sealHeroMediaHydrated = 'true';
}

function queueAdjacentHeroMedia(section) {
  if (navigator.connection?.saveData || document.visibilityState === 'hidden') return;

  const slides = [...section.querySelectorAll(`${slideSelector}:not([data-seal-loop-clone])`)];
  const nextSlide = slides.find((slide) => slide.querySelector(deferredMediaSelector));
  if (!nextSlide) return;

  const hydrate = () => {
    hydrateHeroMedia(nextSlide);

    if (window.matchMedia('(min-width: 750px)').matches) {
      section.querySelectorAll('[data-seal-loop-clone]').forEach(hydrateHeroMedia);
    }
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(hydrate, { timeout: 1800 });
  } else {
    window.setTimeout(hydrate, 900);
  }
}

function prepareHeroMedia(section) {
  if (section.dataset.sealHeroMediaReady === 'true') return;

  section.dataset.sealHeroMediaReady = 'true';
  const firstImage = section.querySelector(`${slideSelector}:not([data-seal-loop-clone]) .seal-home-hero__image`);
  const queue = () => queueAdjacentHeroMedia(section);

  if (!(firstImage instanceof HTMLImageElement) || firstImage.complete) {
    queue();
  } else {
    firstImage.addEventListener('load', queue, { once: true });
    firstImage.addEventListener('error', queue, { once: true });
  }

  section.addEventListener('slideshow:select', (event) => {
    hydrateHeroMedia(event.detail?.slide);
    queueAdjacentHeroMedia(section);
  });

  const hydrateOnControlIntent = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('slideshow-controls, slideshow-arrows')) return;
    section.querySelectorAll(`${slideSelector}:not([data-seal-loop-clone])`).forEach(hydrateHeroMedia);
  };

  section.addEventListener('pointerover', hydrateOnControlIntent, { passive: true });
  section.addEventListener('touchstart', hydrateOnControlIntent, { passive: true });
  section.addEventListener('focusin', hydrateOnControlIntent);
}

function stopSlideSelection(event) {
  event.stopPropagation();
}

function prepareOverlayLink(link) {
  if (link.dataset.sealSlideLinkReady === 'true') return;

  link.addEventListener('click', stopSlideSelection);
  link.dataset.sealSlideLinkReady = 'true';
}

function createSlideLinkFromButton(slide, button) {
  if (slide.querySelector(overlaySelector)) return;

  const href = button.getAttribute('href');
  if (!href) return;

  const link = document.createElement('a');
  link.className = 'seal-fullscreen-slideshow__slide-link';
  link.href = href;
  link.setAttribute('aria-hidden', 'true');
  link.tabIndex = -1;

  if (button.target === '_blank') {
    link.target = '_blank';
    link.rel = button.rel || 'noopener noreferrer';
  }

  const content = slide.querySelector('.slide__content');
  slide.insertBefore(link, content);
  prepareOverlayLink(link);
}

function syncSlideLinks(root = document) {
  root.querySelectorAll(sectionSelector).forEach((section) => {
    prepareHeroMedia(section);
    section.querySelectorAll(`${slideSelector}:not([data-seal-loop-clone])`).forEach((slide) => {
      slide.querySelectorAll(overlaySelector).forEach((link) => {
        if (link instanceof HTMLAnchorElement) prepareOverlayLink(link);
      });

      const button = slide.querySelector(buttonSelector);
      if (button instanceof HTMLAnchorElement) createSlideLinkFromButton(slide, button);
    });
  });
}

if (!customElements.get('seal-loop-slideshow')) {
  customElements.define('seal-loop-slideshow', SealLoopSlideshow);
}

syncSlideLinks();

document.addEventListener('shopify:section:load', (event) => {
  syncSlideLinks(event.target);
});
